import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const AwardWalletSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [status, setStatus] = useState({ configured: false, enabled: false });

  // Parsing priority summary state
  const [parsingPriority, setParsingPriority] = useState({
    preferred_parser: 'awardwallet_primary',
    enable_fallback: true
  });

  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    comparison_mode: true,
    store_results: true,
    auto_compare: true,
    callback_enabled: false,
    timeout_seconds: 30,
    max_poll_retries: 10,
    poll_interval: 2.0,
    supported_document_types: ['flight', 'hotel', 'car_rental', 'rail', 'cruise'],
    beta_organizations: [],
    notify_on_mismatch: false,
    mismatch_notification_email: '',
    mismatch_threshold_percent: 80.0
  });

  const [newBetaOrg, setNewBetaOrg] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadStatus();
      loadSettings();
      loadParsingPriority();
    }
  }, [user]);

  const loadParsingPriority = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/email-parsing/settings`, { headers });
      if (response.data.settings) {
        setParsingPriority({
          preferred_parser: response.data.settings.preferred_parser || 'awardwallet_primary',
          enable_fallback: response.data.settings.enable_fallback ?? true
        });
      }
    } catch (err) {
      console.error('Error loading parsing priority:', err);
    }
  };

  const getPriorityLabel = (value) => {
    const labels = {
      'awardwallet_primary': 'AwardWallet Primary',
      'ai_primary': 'AI Primary',
      'awardwallet_only': 'AwardWallet Only',
      'ai_only': 'AI Only',
      'best_result': 'Best Result'
    };
    return labels[value] || value;
  };

  const loadStatus = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/awardwallet-parsing/status`, { headers });
      setStatus({
        configured: response.data.configured,
        enabled: response.data.enabled
      });
    } catch (err) {
      console.error('Error loading status:', err);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/awardwallet-parsing/settings`, { headers });
      if (response.data.settings) {
        setSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/awardwallet-parsing/settings`, settings, { headers });
      setSuccessMessage('Settings saved successfully!');
      loadStatus();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccessMessage('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Simple test with sample email content
      const testContent = `
        <html>
          <body>
            <h1>Flight Confirmation</h1>
            <p>Confirmation Number: ABC123</p>
            <p>Flight: AA 100</p>
            <p>From: JFK to LAX</p>
            <p>Date: January 15, 2025</p>
          </body>
        </html>
      `;

      const response = await axios.post(
        `${apiBase}/admin/awardwallet-parsing/test`,
        { email_content: testContent },
        { headers }
      );

      if (response.data.success) {
        setSuccessMessage('AwardWallet API test successful! Check the result in the console.');
        console.log('AwardWallet test result:', response.data.result);
      } else {
        setError('Test failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error testing AwardWallet:', err);
      setError('Test failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTesting(false);
    }
  };

  const addBetaOrg = () => {
    if (newBetaOrg && !settings.beta_organizations.includes(newBetaOrg)) {
      setSettings(prev => ({
        ...prev,
        beta_organizations: [...prev.beta_organizations, newBetaOrg]
      }));
      setNewBetaOrg('');
    }
  };

  const removeBetaOrg = (org) => {
    setSettings(prev => ({
      ...prev,
      beta_organizations: prev.beta_organizations.filter(o => o !== org)
    }));
  };

  const toggleDocumentType = (type) => {
    setSettings(prev => ({
      ...prev,
      supported_document_types: prev.supported_document_types.includes(type)
        ? prev.supported_document_types.filter(t => t !== type)
        : [...prev.supported_document_types, type]
    }));
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6 bg-red-50 text-red-600 rounded-lg">
        Access denied. Superadmin privileges required.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading settings...</span>
      </div>
    );
  }

  const allDocTypes = ['flight', 'hotel', 'car_rental', 'rail', 'cruise'];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">AwardWallet Parsing Settings</h1>
        <p className="mt-2 text-gray-600">
          Configure AwardWallet Parsing for comparison with AI parser results.
          When enabled, emails are parsed by both AI and AwardWallet Parsing in parallel for evaluation.
        </p>
        <p className="mt-1 text-sm text-amber-600">
          Note: AwardWallet Parsing is a separately licensed service from AwardWallet Account Access.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {/* API Status */}
      <div className="mb-6 p-4 bg-gray-50 border rounded-lg">
        <h2 className="text-lg font-semibold mb-3">API Status</h2>
        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${status.configured ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>API Configured: {status.configured ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${status.enabled ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            <span>Parsing Enabled: {status.enabled ? 'Yes' : 'No'}</span>
          </div>
        </div>
        {!status.configured && (
          <p className="mt-2 text-sm text-amber-600">
            Set AWARDWALLET_PARSING_API_USER and AWARDWALLET_PARSING_API_PASSWORD, or AWARDWALLET_PARSING_API_KEY (in username:password format).
            Note: AwardWallet Parsing requires separate credentials from the Account Access API.
          </p>
        )}
      </div>

      {/* Parsing Priority Summary */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-blue-900">Parsing Priority</h2>
            <p className="text-sm text-blue-700 mt-1">
              Current setting: <strong>{getPriorityLabel(parsingPriority.preferred_parser)}</strong>
              {parsingPriority.enable_fallback && !parsingPriority.preferred_parser.includes('only') && (
                <span className="ml-2">(with fallback enabled)</span>
              )}
            </p>
          </div>
          <button
            onClick={() => navigate('/email-parsing-settings')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center"
          >
            <span className="mr-1">⚙️</span>
            Configure Priority
          </button>
        </div>
        <p className="text-xs text-blue-600 mt-2">
          Control whether AI or AwardWallet results are preferred when both parsers succeed.
        </p>
      </div>

      {/* Master Toggle */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Enable AwardWallet Parsing</h2>
            <p className="text-sm text-gray-600">
              Master toggle for running AwardWallet parsing in parallel with AI
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
              disabled={!status.configured}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
          </label>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Feature Options</h2>
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <span className="font-medium">Comparison Mode</span>
              <p className="text-sm text-gray-500">Run alongside AI parser (vs. standalone)</p>
            </div>
            <input
              type="checkbox"
              checked={settings.comparison_mode}
              onChange={(e) => setSettings(prev => ({ ...prev, comparison_mode: e.target.checked }))}
              className="w-5 h-5 text-blue-600 rounded"
            />
          </label>
          <label className="flex items-center justify-between">
            <div>
              <span className="font-medium">Store Results</span>
              <p className="text-sm text-gray-500">Save comparison results for review</p>
            </div>
            <input
              type="checkbox"
              checked={settings.store_results}
              onChange={(e) => setSettings(prev => ({ ...prev, store_results: e.target.checked }))}
              className="w-5 h-5 text-blue-600 rounded"
            />
          </label>
          <label className="flex items-center justify-between">
            <div>
              <span className="font-medium">Auto Compare</span>
              <p className="text-sm text-gray-500">Automatically compare field values</p>
            </div>
            <input
              type="checkbox"
              checked={settings.auto_compare}
              onChange={(e) => setSettings(prev => ({ ...prev, auto_compare: e.target.checked }))}
              className="w-5 h-5 text-blue-600 rounded"
            />
          </label>
        </div>
      </div>

      {/* Document Types */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Supported Document Types</h2>
        <p className="text-sm text-gray-600 mb-3">
          Select which document types to send to AwardWallet for parsing
        </p>
        <div className="flex flex-wrap gap-3">
          {allDocTypes.map(type => (
            <button
              key={type}
              onClick={() => toggleDocumentType(type)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                settings.supported_document_types.includes(type)
                  ? 'bg-blue-100 border-blue-300 text-blue-800'
                  : 'bg-gray-100 border-gray-300 text-gray-600'
              }`}
            >
              {type.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* API Settings */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">API Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeout (seconds)
            </label>
            <input
              type="number"
              value={settings.timeout_seconds}
              onChange={(e) => setSettings(prev => ({ ...prev, timeout_seconds: parseInt(e.target.value) || 30 }))}
              className="w-full px-3 py-2 border rounded-lg"
              min="5"
              max="120"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Poll Retries
            </label>
            <input
              type="number"
              value={settings.max_poll_retries}
              onChange={(e) => setSettings(prev => ({ ...prev, max_poll_retries: parseInt(e.target.value) || 10 }))}
              className="w-full px-3 py-2 border rounded-lg"
              min="1"
              max="30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Poll Interval (seconds)
            </label>
            <input
              type="number"
              step="0.5"
              value={settings.poll_interval}
              onChange={(e) => setSettings(prev => ({ ...prev, poll_interval: parseFloat(e.target.value) || 2.0 }))}
              className="w-full px-3 py-2 border rounded-lg"
              min="0.5"
              max="10"
            />
          </div>
        </div>
      </div>

      {/* Beta Organizations */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Beta Organizations</h2>
        <p className="text-sm text-gray-600 mb-3">
          Limit AwardWallet parsing to specific organizations. Leave empty to enable for all.
        </p>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newBetaOrg}
            onChange={(e) => setNewBetaOrg(e.target.value)}
            placeholder="Enter organization ID"
            className="flex-1 px-3 py-2 border rounded-lg"
          />
          <button
            onClick={addBetaOrg}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add
          </button>
        </div>
        {settings.beta_organizations.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {settings.beta_organizations.map(org => (
              <span key={org} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                {org}
                <button
                  onClick={() => removeBetaOrg(org)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No beta organizations configured (enabled for all when feature is on)</p>
        )}
      </div>

      {/* Mismatch Notifications */}
      <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Mismatch Notifications</h2>
        <label className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={settings.notify_on_mismatch}
            onChange={(e) => setSettings(prev => ({ ...prev, notify_on_mismatch: e.target.checked }))}
            className="w-5 h-5 text-blue-600 rounded mr-3"
          />
          <span>Notify when AI and AwardWallet results differ significantly</span>
        </label>
        {settings.notify_on_mismatch && (
          <div className="space-y-4 pl-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notification Email
              </label>
              <input
                type="email"
                value={settings.mismatch_notification_email || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, mismatch_notification_email: e.target.value }))}
                placeholder="alerts@example.com"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mismatch Threshold (%)
              </label>
              <p className="text-sm text-gray-500 mb-2">
                Notify when match score falls below this threshold
              </p>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.mismatch_threshold_percent}
                onChange={(e) => setSettings(prev => ({ ...prev, mismatch_threshold_percent: parseFloat(e.target.value) }))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500">
                <span>0%</span>
                <span className="font-medium text-blue-600">{settings.mismatch_threshold_percent}%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <button
          onClick={handleTest}
          disabled={testing || !status.configured}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
        >
          {testing ? 'Testing...' : 'Test API Connection'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AwardWalletSettings;
